// src/components/LoginForm.tsx
import { useEffect, useMemo, useState } from "react";
import useInscrito from "../../core/hooks/useInscrito";
import DefaultLayout from "../layouts/default/Default.layout";
import { Simulado } from "../../sdk/@types";
import { Column, useTable } from "react-table";
import { Button, Modal, Tooltip } from "antd";
import Table from "../components/Table/Table";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";

/**
 * `true` quando a prova ainda vai acontecer.
 *
 * Abrir o resultado de um simulado que não começou leva a uma tela sem nada
 * para mostrar — não há respostas, acertos nem colocação. O olho fica
 * desabilitado nesse caso.
 *
 * A data olhada é a do SIMULADO (`mocktest.start`), não a do inscrito: a
 * coluna "Início" da tabela mostra `registry_date`, que é quando o aluno se
 * inscreveu, e pode estar bem no passado enquanto a prova ainda está por vir.
 *
 * Sem `start` legível o botão continua habilitado: na dúvida, é melhor deixar
 * abrir e mostrar uma tela vazia do que bloquear um resultado que existe.
 */
function simuladoAindaNaoComecou(inscrito: Simulado.Inscrito): boolean {
  const inicio = inscrito.mocktest?.start;
  if (!inicio) return false;
  const quando = new Date(inicio).getTime();
  if (Number.isNaN(quando)) return false;
  return quando > Date.now();
}

export default function HistoricoSimulado() {
  const {fetchInscritos, inscritos, fetchRankeds, rankeds} = useInscrito();
  const navigate = useNavigate();
  const [showRank, setShowRank] = useState(false);
  const [descricaoSimulado, setDescricaoSimulado] = useState('');

  const columns : Column<Simulado.Inscrito>[] = useMemo(() => [
    {
      Header: 'Descrição',
      accessor: 'mocktest',
      Cell: ({ value }) =>
        value ? (
          <strong>
            {`${value.description}`}
          </strong>
        ) : (
          'Indefinido'
        ),
    },
    {
      Header: 'Nome no Rank',
      accessor: 'nick_name',
    },
    {
      Header: 'Situação',
      accessor: 'situation',
    },
    {
      Header: 'Início',
      accessor: 'registry_date',
      Cell: ({ value }) =>
        value ? (
          <strong>
            {`${new Date(value).toLocaleDateString('pt-BR')} ${new Date(value).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}`}
          </strong>
        ) : (
          'Indefinido'
        ),
    },
    {
      Header: 'Término',
      accessor: 'end',
      Cell: ({ value }) =>
        value ? (
          <strong>
            {`${new Date(value).toLocaleDateString('pt-BR')} ${new Date(value).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}`}
          </strong>
        ) : (
          'Indefinido'
        ),
    },
    {
      Header: 'Qtde Acertos',
      accessor: 'total_correct',
      Cell: ({ value }) =>
        value !== undefined && value !== null ? (
          <strong>{value}</strong>
        ) : (
          'Indefinido'
        ),
    },
    {
      Header: 'Colocação',
      accessor: 'rank', // still needed for sorting/filtering
      Cell: ({ row }) => {
        const item = row.original; // full object here ✅
        return (
          <div className="flex justify-center items-center gap-2">
            <strong>{`${item.rank ?? '–'}º Lugar`}</strong>
            <Button
              type="default"
              icon={<Trophy size={18} />}
              onClick={() => handleRank(item.mocktest?.id!, item.mocktest?.description!)}
            />
          </div>
        );
      },
    },
    {
      Header: 'Ação',
      Cell: ({ row }) => {
        const inscrito = row.original;
        const naoComecou = simuladoAindaNaoComecou(inscrito);

        return (
          <div className="justify-center flex gap-2">
            {/* O Tooltip precisa do <span>: um Button desabilitado do antd não
                emite eventos de ponteiro, então sem o invólucro a explicação
                nunca apareceria — que é justamente quando ela é necessária. */}
            <Tooltip
              title={
                naoComecou
                  ? 'O simulado ainda não começou — não há resultado para ver.'
                  : 'Ver resultado'
              }
            >
              <span>
                <Button
                  type={"default"}
                  disabled={naoComecou}
                  aria-label={
                    naoComecou
                      ? 'Resultado indisponível: o simulado ainda não começou'
                      : 'Ver resultado do simulado'
                  }
                  data-testid="historico-ver-resultado"
                  data-nao-comecou={naoComecou ? 'true' : 'false'}
                  onClick={() => navigate(`/resultado-simulado/${inscrito.id}`)}
                  icon={<EyeOutlined />}
                />
              </span>
            </Tooltip>
          </div>
        );
      },
    }
  ], []);

  const instance = useTable<Simulado.Inscrito>({
    columns,
    data: inscritos??[],
  });

  const columnsRank : Column<Simulado.Inscrito>[] = useMemo(() => [
    {
      Header: 'Colocação',
      accessor: 'rank',
      Cell: ({ value }) =>
        value ? (
          <strong>
            {`${value} º Lugar`}
          </strong>
        ) : (
          'Indefinido'
        ),
    },
    {
      Header: 'Participante',
      accessor: 'user',
      Cell: ({ row }) => {
        const record = row.original;

        // If nick_name exists, override userName
        const userName =
          record.nick_name ??
          record.user?.userName ??
          "Indefinido";

        return <strong>{userName}</strong>;
      },
    },
    {
      Header: 'Acertos',
      accessor: 'total_correct',
      Cell: ({ value }) =>
        value !== undefined && value !== null ? (
          <strong>{value}</strong>
        ) : (
          'Indefinido'
        ),
    },
  ], []);

  const instanceRank = useTable<Simulado.Inscrito>({
    columns: columnsRank,
    data: rankeds??[],
  });

  useEffect(()=>{
    const load = async () =>{
      await fetchInscritos();
    }
    load();
  },[fetchInscritos]);  

  const handleRank = (mocktestID : string, mocktestDescription : string) =>{
    fetchRankeds(mocktestID);
    setShowRank(true);
    setDescricaoSimulado(mocktestDescription);
  }

  return (
    <DefaultLayout>
      <div className="justify-start mb-2 border-b dark:border-slate-700 p-2">
        <p className="text-2xl font-bold text-gray-500 dark:text-gray-300">Simulados</p>
        <p className="text-1x1 font-normal text-gray-500 dark:text-gray-300">
          Histórico de Simulados Realizados
        </p>
      </div>

      <Table instance={instance} />

      <Modal
        open={showRank}
        onCancel={() => setShowRank(false)}
        width={480}
        footer={null}
        centered
        destroyOnClose
        title={
          <span className="font-semibold text-gray-700 dark:text-gray-200">
            Ranking dos Participantes
          </span>
        }
      >
        <span className="text-2xl text-gray-700 dark:text-gray-200 mb-4">
          {descricaoSimulado}
        </span>
        <div className="max-h-[60vh] overflow-y-auto mt-2">
          <Table
            instance={instanceRank}
          />
        </div>
      </Modal>
    </DefaultLayout>  
  );
}
